// ---------------------------------------------------------------------------
// Esqueci minha senha — fluxo público de dois passos (handoff 0098).
//
// UMA rota, DOIS estágios, sem navegação intermediária: o código copiado do
// e-mail é colado sem o formulário perder contexto (0098 §5).
//
// Decisões que vêm do contrato e não devem ser "melhoradas" na UI:
//  - O passo 1 responde 200 sempre, inclusive para e-mail inexistente. O avanço
//    para o estágio 2 é incondicional (§2).
//  - Tudo que envolve o código — errado, expirado, já usado, desafio queimado —
//    tem UMA mensagem genérica (§3). Distinguir os casos ajuda quem adivinha.
//  - Sucesso revoga todas as sessões; se houver estado local, é limpo antes de
//    ir para o login (§4).
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { isEmail } from 'validator';

import {
   requestPasswordReset,
   confirmPasswordReset,
} from '../../../services/auth/authApi';
import { endSession } from '../../../store/modules/auth/sessionCleanup';
import { notifyApiError } from '../../../utils/feedback';

import Loading from '../../../components/Loading';
import logoMyshop from '../../../assets/logo-myshop.png';

import {
   AuthPage,
   AuthContainer,
   AuthCard,
   AuthCardTop,
   AuthCardBody,
   AuthBrand,
   AuthLogo,
   AuthTitle,
   AuthSubtitle,
} from '../../../styles/auth/AuthLayout';

import EmailStage from './components/EmailStage';
import CodeStage from './components/CodeStage';

const STAGE_EMAIL = 'email';
const STAGE_CODE = 'code';

// Espelham o contrato do backend (0098 §4 e §2).
const CODE_TTL_SECONDS = 15 * 60;
// Cooldown LOCAL do reenviar. O limite real é 5 pedidos / 10 min (por IP +
// e-mail); o cooldown existe para o usuário não queimar a cota com cliques
// repetidos, não para substituir o rate limit do servidor.
const RESEND_COOLDOWN_SECONDS = 60;

const EMPTY_CONFIRM_FORM = {
   code: '',
   new_password: '',
   new_password_confirm: '',
};

const PASSWORD_MIN = 6;
const PASSWORD_MAX = 50;

function formatCountdown(totalSeconds) {
   const safe = Math.max(0, totalSeconds);
   const minutes = String(Math.floor(safe / 60)).padStart(2, '0');
   const seconds = String(safe % 60).padStart(2, '0');
   return `${minutes}:${seconds}`;
}

function validateConfirmLocally(form) {
   const errors = {};

   if (!/^\d{6}$/.test(form.code)) {
      errors.code = 'O código tem 6 dígitos.';
   }

   if (!form.new_password) {
      errors.new_password = 'Informe a nova senha.';
   } else if (
      form.new_password.length < PASSWORD_MIN ||
      form.new_password.length > PASSWORD_MAX
   ) {
      errors.new_password = `A senha precisa ter entre ${PASSWORD_MIN} e ${PASSWORD_MAX} caracteres.`;
   }

   if (form.new_password_confirm !== form.new_password) {
      errors.new_password_confirm = 'A confirmação não confere.';
   }

   return errors;
}

export default function ForgotPassword() {
   const navigate = useNavigate();
   const dispatch = useDispatch();

   const [stage, setStage] = useState(STAGE_EMAIL);
   const [email, setEmail] = useState('');
   const [emailError, setEmailError] = useState(null);

   const [form, setForm] = useState(EMPTY_CONFIRM_FORM);
   const [fieldErrors, setFieldErrors] = useState({});

   const [isSubmitting, setIsSubmitting] = useState(false);
   const [secondsLeft, setSecondsLeft] = useState(0);
   const [resendCooldown, setResendCooldown] = useState(0);

   // Guarda para não atualizar estado depois que a tela sai (o sucesso navega).
   const isMounted = useRef(true);
   useEffect(
      () => () => {
         isMounted.current = false;
      },
      []
   );

   // Um tique por segundo alimenta o contador de validade E o cooldown do
   // reenviar — dois timers para a mesma cadência seria desperdício.
   useEffect(() => {
      if (stage !== STAGE_CODE) return undefined;

      const timer = setInterval(() => {
         setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
         setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);

      return () => clearInterval(timer);
   }, [stage]);

   const countdownLabel = useMemo(
      () => formatCountdown(secondsLeft),
      [secondsLeft]
   );

   const handleFieldChange = useCallback(
      (field) => (event) => {
         const { value } = event.target;
         // O código só aceita dígito — colar "483 920" ou "483-920" do e-mail
         // não pode quebrar o envio.
         const nextValue =
            field === 'code' ? value.replace(/\D/g, '').slice(0, 6) : value;

         setForm((prev) => ({ ...prev, [field]: nextValue }));
         setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
         });
      },
      []
   );

   // Passo 1. `silent` = reenvio a partir do estágio 2 (não reinicia a tela).
   const sendCode = useCallback(
      async (targetEmail, { silent = false } = {}) => {
         setIsSubmitting(true);
         try {
            await requestPasswordReset(targetEmail);

            if (!isMounted.current) return;

            // Resposta cega: avança SEMPRE, sem revelar se o e-mail existe.
            setStage(STAGE_CODE);
            setSecondsLeft(CODE_TTL_SECONDS);
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
            // Código novo invalida o anterior (§4): limpar o campo evita que o
            // usuário reenvie e tente o código velho que ficou na tela.
            setForm((prev) => ({ ...prev, code: '' }));
            setFieldErrors({});

            toast.info(
               silent
                  ? 'Novo código enviado. O código anterior deixou de valer.'
                  : 'Se o e-mail estiver cadastrado, enviamos um código de redefinição.'
            );
         } catch (err) {
            if (!isMounted.current) return;
            // 422 `format` vem com field `email` → inline.
            if (err?.fieldErrors?.email) setEmailError(err.fieldErrors.email);
            // 429 (5 pedidos / 10 min) cai em toast, sem retry automático.
            notifyApiError(err);
         } finally {
            if (isMounted.current) setIsSubmitting(false);
         }
      },
      []
   );

   const handleRequestSubmit = (event) => {
      event.preventDefault();
      if (isSubmitting) return;

      const trimmed = email.trim();
      if (!isEmail(trimmed)) {
         setEmailError('Informe um e-mail válido.');
         return;
      }

      setEmailError(null);
      setEmail(trimmed);
      sendCode(trimmed);
   };

   const handleConfirmSubmit = async (event) => {
      event.preventDefault();
      if (isSubmitting) return;

      const localErrors = validateConfirmLocally(form);
      if (Object.keys(localErrors).length > 0) {
         setFieldErrors(localErrors);
         return;
      }

      setFieldErrors({});
      setIsSubmitting(true);

      try {
         await confirmPasswordReset({
            email,
            code: form.code,
            newPassword: form.new_password,
            newPasswordConfirm: form.new_password_confirm,
         });

         toast.success('Senha redefinida com sucesso. Entre com a nova senha.');

         // O reset revogou tudo no backend. O usuário provavelmente já está
         // deslogado, mas se houver estado local ele morre aqui.
         // `redirectTo: null` porque a navegação é do react-router: um
         // `window.location` descartaria o e-mail que vai preencher o login.
         endSession(dispatch, { redirectTo: null });
         navigate('/login', { state: { email }, replace: true });
      } catch (err) {
         if (!isMounted.current) return;
         if (err?.fieldErrors && Object.keys(err.fieldErrors).length > 0) {
            setFieldErrors(err.fieldErrors);
         }
         notifyApiError(err);
         setIsSubmitting(false);
      }
   };

   const handleUseAnotherEmail = () => {
      setStage(STAGE_EMAIL);
      setForm(EMPTY_CONFIRM_FORM);
      setFieldErrors({});
      setSecondsLeft(0);
      setResendCooldown(0);
   };

   const isCodeStage = stage === STAGE_CODE;

   return (
      <AuthPage>
         <AuthContainer $size="lg">
            <Loading isLoading={isSubmitting} />

            {/*
               Sem `AuthGrid`/`AuthVisual`: esta tela é só o card, centralizado.
               O painel ilustrado do login tem altura fixa de 560px e esticava
               junto com o card no estágio 2, que é bem mais alto que o
               formulário de login. Aqui ele também não paga o próprio espaço —
               a tarefa é colar um código do e-mail, não vender o produto.

               O estágio 2 usa card largo porque divide o conteúdo em duas
               colunas (ver CodeStage); o estágio 1, de um campo só, fica na
               largura do card de login.
            */}
            <AuthCard $fluid $maxWidth={isCodeStage ? 820 : 460}>
               <AuthCardTop>
                  <AuthBrand>
                     <AuthLogo src={logoMyshop} alt="MyShop" />
                  </AuthBrand>
               </AuthCardTop>

               <AuthCardBody>
                  <AuthTitle>Esqueci minha senha</AuthTitle>
                  <AuthSubtitle>
                     {isCodeStage
                        ? 'Informe o código recebido e escolha uma nova senha.'
                        : 'Informe o e-mail da sua conta para receber um código. Sua senha atual continua valendo até você usar o código.'}
                  </AuthSubtitle>

                  {isCodeStage ? (
                     <CodeStage
                        email={email}
                        form={form}
                        fieldErrors={fieldErrors}
                        onFieldChange={handleFieldChange}
                        onSubmit={handleConfirmSubmit}
                        onUseAnotherEmail={handleUseAnotherEmail}
                        onResend={() => sendCode(email, { silent: true })}
                        isSubmitting={isSubmitting}
                        countdownLabel={countdownLabel}
                        isExpired={secondsLeft === 0}
                        resendCooldown={resendCooldown}
                     />
                  ) : (
                     <EmailStage
                        email={email}
                        onEmailChange={(e) => {
                           setEmail(e.target.value);
                           setEmailError(null);
                        }}
                        onSubmit={handleRequestSubmit}
                        error={emailError}
                        isSubmitting={isSubmitting}
                     />
                  )}
               </AuthCardBody>
            </AuthCard>
         </AuthContainer>
      </AuthPage>
   );
}
